import type React from 'react'
import { toast } from 'sonner'
import type { SlashSkillRow } from '@/components/ui/slash-command-menu'
import { API_BASE_URL, getActiveConversations, autoNameConversation, fetchWithAuth } from '@/lib/api'
import { patchChatMessageMetadata } from '@/lib/chat-message-metadata'
import { getLocalCalendarDate, getClientTimezone } from '@/lib/utils'
import {
  refreshChatsSidebar,
  sidebarUpdateForAgentChatThread,
  touchChatSidebarThread,
} from '@/lib/chats-sidebar-events'
import { devLog } from '@/lib/dev-log'
import {
  CHAT_PAYLOAD_TOAST_TITLE,
  validateChatStreamPayload,
} from '@/lib/chat-image-budget'
import { upsertCompletedFile } from '@/lib/chat-completed-files'
import type {
  Message,
  DocumentReference,
  ToolCallResult,
  ActionChoice,
  AskQuestionData,
  FlashCardSessionData,
  QuizDocumentCreatedData,
  ProposedEditData,
  ViewContext,
} from './chat-pane.types'
import {
  indexOfStreamingAssistantSlot,
  inferCappedAtFromAssistantContent,
  prependMissingUserTurns,
  stripAgenticPauseProse,
} from './chat-pane.utils'
import { buildTouchedFilesMetadata } from '@/lib/chat-message-hydration'
import {
  buildToolTraceMetadata,
  serializeChatHistoryForStream,
} from '@/lib/chat-history-for-stream'
import { normalizeChangedLines } from './chat-pane.handlers'
import {
  formatChatStreamError,
  formatChatStreamTransportError,
  isChatStreamTransportError,
} from '@/lib/chat-stream-error-display'
import { createStreamStallWatchdog } from '@/lib/chat-stream-stall-watchdog'
import {
  assistantTextWorthKeeping,
  CHAT_STREAM_REPLY_KEPT_TOAST,
  decideChatStreamTailRecovery,
} from '@/lib/chat-stream-tail-recovery'
import { enrichActionChoices } from '@/lib/chat-action-choice'
import {
  attachFillCommentChoice,
  getActiveTodoActionSession,
} from '@/lib/open-basecamp-todo-action-chat'
import { revealTextProgressively } from '@/lib/progressive-text-reveal'
import type { BasecampNotesDraft } from '@/components/integrations/basecamp/components/basecamp-notes-draft-panel'
import type { BasecampCreateTodoDraft } from '@/lib/basecamp-create-todo-draft'
import {
  parseProposedBasecampCreateEvent,
  persistBasecampCreateDraft,
} from '@/lib/basecamp-create-todo-draft'
import { dispatchBasecampTodoRefreshFromNotesWrite } from '@/lib/basecamp-ingestion-events'

export type ThinkingOperation = {
  id: string
  type: 'search' | 'search_files' | 'read' | 'mcp_tool' | 'tool_call' | 'persona'
  label: string
  status: 'active' | 'completed' | 'error'
  result?: string
  timestamp?: string
  personaName?: string
  personaHint?: string
}

export type CompletedFileRow = {
  /** Internal storage path (e.g. ws/{orgId}/{slug}.md) — never render directly; use displayPath. */
  filePath: string
  /** Human-facing "{workspaceSlug}/{docSlug}" path from the backend, when available. */
  displayPath?: string | null
  /** Full shareable webapp URL, when available. */
  url?: string | null
  status: 'new' | 'modified'
  linesAdded: number
  linesRemoved: number
  documentId?: string
  timestamp?: string
  title?: string
  changeStatus?: string
}

export type ActiveConversationRow = {
  id: string
  title: string
  message_count?: number
  updated_at?: string
  created_at?: string
}

export type ExecuteGeneralChatStreamParams = {
  abortController: AbortController
  abortControllerRef: React.MutableRefObject<AbortController | null>
  /** When present, UI writes no-op after New chat / conversation switch bumps the epoch. */
  streamEpochRef?: React.MutableRefObject<number>
  savedConvId: string | null | undefined
  messagesForHistory: Message[]
  referencesForRequest: DocumentReference[]
  activeDocumentIdRef: React.RefObject<string | undefined>
  dismissedDocIdsRef: React.RefObject<Set<string>>
  viewContext: ViewContext | undefined
  workspaceScopePaths: string[]
  selectedProvider: 'openai' | 'anthropic' | 'groq' | 'xai' | 'localai' | 'ollama'
  selectedModel: string
  chatMode: 'ask' | 'plan' | 'agent'
  activeChatSkill: { id: string; name: string } | null
  streamingConversationId: string | null
  scopedAvailableSkills: SlashSkillRow[]
  setStreamingMessages: (messages: Message[] | ((prev: Message[]) => Message[])) => void
  setIsStreaming: (streaming: boolean) => void
  setIsTyping: (typing: boolean) => void
  setThinkingModeActive: (active: boolean) => void
  setThinkingOperations: React.Dispatch<React.SetStateAction<ThinkingOperation[]>>
  setCurrentTask: React.Dispatch<
    React.SetStateAction<{
      action: string
      /** Internal storage path (e.g. ws/{orgId}/{slug}.md) — never render directly; use displayPath. */
      filePath: string
      /** Human-facing "{workspaceSlug}/{docSlug}" path from the backend, when available. */
      displayPath?: string | null
      /** Full shareable webapp URL, when available. */
      url?: string | null
      content: string
      isWriting: boolean
      timestamp?: string
    } | null>
  >
  setCompletedFiles: React.Dispatch<React.SetStateAction<CompletedFileRow[]>>
  setProposedEdit: React.Dispatch<React.SetStateAction<ProposedEditData | null>>
  /** Called once when the stream ends with every proposed edit from this request. */
  onStreamProposedEditsComplete?: (edits: ProposedEditData[]) => void
  setPendingBasecampDraft?: React.Dispatch<React.SetStateAction<BasecampNotesDraft | null>>
  setPendingBasecampCreateDraft?: React.Dispatch<React.SetStateAction<BasecampCreateTodoDraft | null>>
  setActiveChatSkill: React.Dispatch<React.SetStateAction<{ id: string; name: string } | null>>
  setSuggestedSkill?: React.Dispatch<React.SetStateAction<SlashSkillRow | null>>
  messagesContainerRef: React.RefObject<HTMLDivElement | null>
  /** When true, stream follow is paused (user scrolled up). Used to avoid hijacking viewport. */
  userHasScrolledRef?: React.RefObject<boolean>
  saveMessageToHistory: (
    role: 'user' | 'assistant',
    content: string,
    conversationIdOverride?: string | null,
    options?: { metadata?: Record<string, unknown> },
  ) => Promise<{ conversationId: string | null; messageId: string | null }>
  activeConversations: ActiveConversationRow[]
  setActiveConversations: React.Dispatch<React.SetStateAction<ActiveConversationRow[]>>
  setHistoryRefreshKey: (key: number | ((prev: number) => number)) => void
  isTypingRef: React.MutableRefObject<boolean>
  /** Placeholder thread id (new-*) when hosted in editor/agent panel; used for SSE migrate. */
  hostedThreadIdRef?: React.MutableRefObject<string | undefined>
  /**
   * Compact agent panel: when this send created a new conversation, bind the
   * ensured thread even if restore raced and hostedThreadId is null or a different id.
   */
  claimAgentPanelOnNewThread?: boolean
  /** After auto-name, update in-session thread title cache (agent panel header + sidebar). */
  onHostedThreadTitleSynced?: (title: string) => void
  agenticContinue?: boolean
  setAgenticRound?: React.Dispatch<
    React.SetStateAction<{ current: number; max: number } | null>
  >
  /** Context window meter (Track D) - latest prompt size vs model window */
  onContextUsage?: (usage: {
    inputTokens: number
    outputTokens: number
    contextWindow: number
    estimated: boolean
    model?: string | null
    pack?: string[]
  }) => void
  /** Internal: auto-retry count after a mid-stream transport drop with no side effects.
   * Callers should omit; executeGeneralChatStream sets this on retry.
   */
  streamRetryAttempt?: number
  hostedLinkedConversationId?: string | null
}

export type BuildChatStreamRequestBodyParams = Pick<
  ExecuteGeneralChatStreamParams,
  | 'messagesForHistory'
  | 'referencesForRequest'
  | 'activeDocumentIdRef'
  | 'dismissedDocIdsRef'
  | 'viewContext'
  | 'workspaceScopePaths'
  | 'selectedProvider'
  | 'selectedModel'
  | 'chatMode'
  | 'activeChatSkill'
  | 'agenticContinue'
  | 'savedConvId'
  | 'streamingConversationId'
  | 'hostedLinkedConversationId'
>

export function buildChatStreamRequestBody(
  params: BuildChatStreamRequestBodyParams & {
    agenticContinue?: boolean
    streamingConversationId?: string | null
  },
) {
  const {
    messagesForHistory,
    referencesForRequest,
    activeDocumentIdRef,
    dismissedDocIdsRef,
    viewContext,
    workspaceScopePaths,
    selectedProvider,
    selectedModel,
    chatMode,
    activeChatSkill,
    agenticContinue,
    savedConvId,
    streamingConversationId,
    hostedLinkedConversationId,
  } = params

  const conversationId =
    savedConvId || streamingConversationId || hostedLinkedConversationId || null

  const serialized = serializeChatHistoryForStream(messagesForHistory, {
    agenticContinue: Boolean(agenticContinue),
  })

  return {
    messages: serialized.map((m) => ({
      role: m.role,
      content: m.content || '',
      ...(m.tool_calls ? { tool_calls: m.tool_calls } : {}),
      ...(m.tool_call_id ? { tool_call_id: m.tool_call_id, name: m.name } : {}),
      ...(m.attachmentIds && m.attachmentIds.length > 0
        ? { attachmentIds: m.attachmentIds }
        : {}),
      ...(m.images && m.images.length > 0
        ? { images: m.images.map((img) => ({ base64: img.base64, mediaType: img.mediaType })) }
        : {}),
    })),
    references: referencesForRequest,
    activeDocumentId:
      activeDocumentIdRef.current &&
      dismissedDocIdsRef.current.has(activeDocumentIdRef.current)
        ? null
        : activeDocumentIdRef.current,
    viewContext,
    workspaceScope: workspaceScopePaths,
    provider: selectedProvider,
    model: selectedModel,
    mode: chatMode,
    skillId: activeChatSkill?.id,
    clientLocalDate: getLocalCalendarDate(),
    clientTimezone: getClientTimezone(),
    ...(conversationId ? { conversationId } : {}),
    ...(agenticContinue ? { agenticContinue: true } : {}),
    ...(hostedLinkedConversationId ? { hostedLinkedConversationId } : {}),
  }
}

export async function executeGeneralChatStream(
  params: ExecuteGeneralChatStreamParams,
): Promise<{ cappedAt?: 'max_rounds' | 'max_tools' | 'incomplete_write' }> {
  const {
    abortController,
    abortControllerRef,
    streamEpochRef,
    savedConvId,
    messagesForHistory,
    referencesForRequest,
    activeDocumentIdRef,
    dismissedDocIdsRef,
    viewContext,
    workspaceScopePaths,
    selectedProvider,
    selectedModel,
    chatMode,
    activeChatSkill,
    streamingConversationId,
    scopedAvailableSkills,
    hostedLinkedConversationId,
    setStreamingMessages: setStreamingMessagesRaw,
    setIsStreaming,
    setIsTyping,
    setThinkingModeActive,
    setThinkingOperations,
    setCurrentTask,
    setCompletedFiles,
    setProposedEdit,
    onStreamProposedEditsComplete,
    setPendingBasecampDraft,
    setPendingBasecampCreateDraft,
    setActiveChatSkill,
    setSuggestedSkill,
    messagesContainerRef,
    userHasScrolledRef,
    saveMessageToHistory,
    activeConversations,
    setActiveConversations,
    setHistoryRefreshKey,
    isTypingRef,
    hostedThreadIdRef,
    onHostedThreadTitleSynced,
    agenticContinue = false,
    setAgenticRound,
    streamRetryAttempt = 0,
    claimAgentPanelOnNewThread = false,
  } = params

  // Pin UI mutations to this stream. New chat / history switch bumps streamEpochRef and aborts;
  // without this, SSE callbacks keep writing into the newly selected conversation (chat merge).
  const streamEpochAtStart = streamEpochRef?.current ?? 0
  const streamStillOwnsPane = () =>
    !streamEpochRef || streamEpochRef.current === streamEpochAtStart
  const setStreamingMessages = (
    update: Message[] | ((prev: Message[]) => Message[]),
  ) => {
    if (!streamStillOwnsPane()) return
    setStreamingMessagesRaw(update)
  }

  const touchedFilesForPersist: CompletedFileRow[] = []

  const recordTouchedFile = (row: CompletedFileRow) => {
    const idx = touchedFilesForPersist.findIndex(
      (f) => f.filePath === row.filePath || (row.documentId && f.documentId === row.documentId),
    )
    if (idx >= 0) {
      touchedFilesForPersist[idx] = { ...touchedFilesForPersist[idx], ...row }
    } else {
      touchedFilesForPersist.push(row)
    }
  }

  /** Backend-only skill match (SSE skill_auto_activated) — clear after stream so the next send does not carry skillId. */
  let autoSkillActivatedThisStream = false
  /** Proposed edits collected during this stream — review opens once at end, not per edit. */
  const sessionProposedEdits: ProposedEditData[] = []
  let streamEndedWithError = false
  /** True once any tool_call SSE arrived - retry would risk duplicate side effects. */
  let sawToolCallEvent = false
  let streamChunkCount = 0
  /** Live cap from SSE (messagesForHistory snapshot is stale — do not read cappedAt from it). */
  let streamCappedAt: 'max_rounds' | 'max_tools' | 'incomplete_write' | undefined
  /** When set, skip finally cleanup and re-enter executeGeneralChatStream once. */
  let pendingRetryController: AbortController | null = null
  let pendingCreateDraftForPersist: BasecampCreateTodoDraft | null = null
  let stallWatchdog: ReturnType<typeof createStreamStallWatchdog> | null = null
  // Hoisted so a reader.read() throw can persist text that already arrived.
  // let/const inside try are not visible in catch.
  let assistantMessage = ''
  let assistantSegmentStart = 0
  let narrationPlaceholderOnly = false
  const toolResults: ToolCallResult[] = []

  const persistAssistantReply = async (
    rawContent: string,
    extras?: {
      askQuestion?: AskQuestionData | null
      flashCardSession?: FlashCardSessionData | null
    },
  ) => {
    const cleaned = stripAgenticPauseProse(rawContent)
    const question = extras?.askQuestion || null
    const cards = extras?.flashCardSession || null
    const body =
      cleaned.trim() ||
      (question ? question.question : '') ||
      (cards ? 'Flash cards' : '')
    if (!body.trim()) return null
    const effectiveCappedAt =
      streamCappedAt ?? inferCappedAtFromAssistantContent(cleaned)
    const touchedMeta = buildTouchedFilesMetadata(touchedFilesForPersist) ?? {}
    const toolTraceMeta = buildToolTraceMetadata(toolResults)
    const metadata = {
      ...touchedMeta,
      ...(toolTraceMeta || {}),
      ...(effectiveCappedAt ? { cappedAt: effectiveCappedAt } : {}),
      ...(pendingCreateDraftForPersist
        ? { basecampCreateDraft: pendingCreateDraftForPersist }
        : {}),
      ...(question ? { askQuestion: question } : {}),
      ...(cards ? { flashCardSession: cards } : {}),
      ...(sessionProposedEdits.some((edit) => edit.status === 'pending')
        ? {
            pendingProposedEdits: sessionProposedEdits.filter((edit) => edit.status === 'pending'),
          }
        : {}),
    }
    const saved = await saveMessageToHistory(
      'assistant',
      body,
      savedConvId ?? undefined,
      Object.keys(metadata).length > 0 ? { metadata } : undefined,
    )
    return saved
  }

  const stampSavedAssistant = async (
    raw: string,
    extras?: {
      askQuestion?: AskQuestionData | null
      flashCardSession?: FlashCardSessionData | null
    },
  ) => {
    const saved = await persistAssistantReply(raw, extras)
    const messageId = saved?.messageId ? String(saved.messageId) : ''
    if (!messageId) return
    setStreamingMessages((prev) => {
      const idx = prev.findIndex(
        (m) => m.role === 'assistant' && m.streamSlotId === activeStreamSlotId,
      )
      if (idx < 0) return prev
      const current = prev[idx]
      const next = [...prev]
      next[idx] = {
        ...current,
        messageId,
        metadata: {
          ...(current.metadata || {}),
          pendingProposedEdits: sessionProposedEdits.filter((edit) => edit.status === 'pending'),
        },
      }
      if (current.askQuestion?.answered) {
        void patchChatMessageMetadata(messageId, { askQuestion: current.askQuestion })
      }
      if (current.flashCardSession) {
        void patchChatMessageMetadata(messageId, { flashCardSession: current.flashCardSession })
      }
      return next
    })
  }

  const maybeAutoNameConversation = async () => {
    if (!savedConvId) return
    const currentConv = activeConversations.find(c => c.id === savedConvId)
    const isDefaultTitle = !currentConv ||
      currentConv.title === 'New Chat' ||
      currentConv.title === 'New Conversation' ||
      currentConv.title.toLowerCase().startsWith('new')

    if (!isDefaultTitle) return
    devLog('[ChatPane] Auto-naming conversation:', savedConvId)
    try {
      const autoNameResult = await autoNameConversation(savedConvId)
      const newTitle = autoNameResult.conversation?.title
      if (newTitle) {
        onHostedThreadTitleSynced?.(newTitle)
      }
      const response = await getActiveConversations()
      if (response.success) {
        setActiveConversations(response.conversations.map(conv => ({
          id: conv.id,
          title: conv.title,
          message_count: conv.message_count,
          updated_at: conv.updated_at,
          created_at: conv.created_at
        })))
        setHistoryRefreshKey(prev => prev + 1)
        refreshChatsSidebar()
      }
    } catch (error) {
      console.error('[ChatPane] Failed to auto-name conversation:', error)
    }
  }

  // Stream-slot helpers MUST live outside try so catch/finally can clear placeholders /
  // paint transport errors (let/const inside try are not in catch/finally scope).
  const streamSlotEpoch = `stream-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
  let streamSlotSeq = 0
  let activeStreamSlotId = `${streamSlotEpoch}-${streamSlotSeq}`
  const nextStreamSlotId = () => {
    streamSlotSeq += 1
    activeStreamSlotId = `${streamSlotEpoch}-${streamSlotSeq}`
    return activeStreamSlotId
  }
  const slotIdx = (msgs: Message[]) =>
    indexOfStreamingAssistantSlot(msgs, activeStreamSlotId)
  const ensureActiveStreamSlot = (msgs: Message[]): { messages: Message[]; index: number } => {
    const restored = prependMissingUserTurns(msgs, messagesForHistory)
    const existing = slotIdx(restored)
    if (existing >= 0) return { messages: restored, index: existing }
    const next = [
      ...restored,
      {
        role: 'assistant' as const,
        content: '',
        timestamp: new Date(),
        toolResults: [] as ToolCallResult[],
        streamSlotId: activeStreamSlotId,
      },
    ]
    return { messages: next, index: next.length - 1 }
  }
  let pendingAskQuestion: AskQuestionData | null = null
  let pendingFlashCardSession: FlashCardSessionData | null = null

  try {
  devLog('[ChatPane] Sending to:', `${API_BASE_URL}/api/chat-stream`);
  devLog('[ChatPane] Provider:', selectedProvider, 'Model:', selectedModel);

  const requestBody = buildChatStreamRequestBody({
    messagesForHistory,
    referencesForRequest,
    activeDocumentIdRef,
    dismissedDocIdsRef,
    viewContext,
    workspaceScopePaths,
    selectedProvider,
    selectedModel,
    chatMode,
    activeChatSkill,
    agenticContinue,
    savedConvId,
    streamingConversationId,
    hostedLinkedConversationId,
  })

  const payloadCheck = validateChatStreamPayload(requestBody)
  if (!payloadCheck.ok) {
    toast.error(CHAT_PAYLOAD_TOAST_TITLE, {
      description: payloadCheck.message,
      duration: 8000,
    })
    throw new Error(payloadCheck.message)
  }

  stallWatchdog = createStreamStallWatchdog({
    onStall: () => {
      if (!abortController.signal.aborted) abortController.abort()
    },
  })

  const response = await fetchWithAuth(`${API_BASE_URL}/api/chat-stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
    signal: abortController.signal
  })

  if (!response.ok) {
    // Try to parse a structured error body for better UX
    try {
      const errBody = await response.json()
      if (errBody.error === 'provider_billing_exhausted' || errBody.error === 'provider_auth_failed') {
        const formatted = formatChatStreamError({
          error: errBody.error,
          message: errBody.message,
          title: errBody.title,
          provider: errBody.provider,
          keySource: errBody.keySource,
          guidance: errBody.guidance,
        })
        throw new Error(formatted.description)
      }
      if (errBody.error === 'free_tier_limit') {
        throw new Error(errBody.message || 'Daily free-tier limit reached. Add your own API key in Workspace Settings.')
      }
      if (errBody.error === 'context_too_large') {
        throw new Error(errBody.message || 'Context too large for this model\'s free tier. Start a new conversation or switch models.')
      }
      if (response.status === 413 || errBody.error === 'payload_too_large') {
        throw new Error(errBody.message || 'This chat turn is too large for the server. Start a new chat, or remove images.')
      }
      throw new Error(errBody.message || errBody.error || `HTTP ${response.status}`)
    } catch (jsonErr) {
      if (jsonErr instanceof SyntaxError) {
        if (response.status === 413) {
          throw new Error('This chat turn is too large for the server. Start a new chat, or remove images.')
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      throw jsonErr
    }
  }

  // Read streaming response
  const reader = response.body?.getReader()
  stallWatchdog?.bump()
  const decoder = new TextDecoder()

  // assistantMessage / assistantSegmentStart / narrationPlaceholderOnly / toolResults
  // are hoisted above try. Segment start seals each round so text stays with that round's tools.
  const documentsMap = new Map<string, DocumentReference>() // Track documents for inline rendering
  let pendingActionChoices: ActionChoice[] | null = null
  let pendingQuizCreated: QuizDocumentCreatedData | null = null

  const flushAssistantStreamingSlot = () => {
    setStreamingMessages((prev) => {
      const ensured = ensureActiveStreamSlot([...prev])
      const newMessages = ensured.messages
      const idx = ensured.index
      const slotEl = newMessages[idx]
      const displayContent = assistantMessage.slice(assistantSegmentStart)

      if (slotEl?.role === 'assistant') {
        const toolResultsChanged =
          JSON.stringify(slotEl.toolResults || []) !== JSON.stringify(toolResults)
        const documentsMapChanged = (slotEl.documentsMap?.size || 0) !== documentsMap.size
        if (
          slotEl.content === displayContent &&
          !toolResultsChanged &&
          !documentsMapChanged &&
          slotEl.narrationPlaceholder === narrationPlaceholderOnly &&
          newMessages.length === prev.length
        ) {
          return prev
        }
        newMessages[idx] = {
          ...slotEl,
          role: 'assistant',
          content: displayContent,
          toolResults: toolResults,
          documentsMap: documentsMap,
          proposedEdit: slotEl.proposedEdit,
          actionChoices: pendingActionChoices ?? slotEl.actionChoices,
          askQuestion: pendingAskQuestion ?? slotEl.askQuestion,
          flashCardSession: pendingFlashCardSession ?? slotEl.flashCardSession,
          quizCreated: pendingQuizCreated ?? slotEl.quizCreated,
          checkpointIds: slotEl.checkpointIds,
          aborted: slotEl.aborted,
          narrationPlaceholder: narrationPlaceholderOnly,
          streamSlotId: activeStreamSlotId,
          timestamp: slotEl.timestamp || new Date(),
        }
      }

      return newMessages
    })
    // Viewport follow is owned by useStreamFollowScroll (honors userHasScrolled).
  }

  const applyNarrationToAssistant = (message: string) => {
    const trimmed = message.trim()
    if (!trimmed) return
    const currentSegment = assistantMessage.slice(assistantSegmentStart).trim()
    // Avoid stacking identical placeholders (dual "Working on it..." shimmer).
    if (currentSegment === trimmed && narrationPlaceholderOnly) return
    assistantMessage = assistantMessage.slice(0, assistantSegmentStart) + trimmed
    narrationPlaceholderOnly = true
    const displayContent = assistantMessage.slice(assistantSegmentStart)
    setStreamingMessages((prev) => {
      const ensured = ensureActiveStreamSlot([...prev])
      const newMessages = ensured.messages
      const idx = ensured.index
      newMessages[idx] = {
        ...newMessages[idx],
        content: displayContent,
        narrationPlaceholder: true,
        streamSlotId: activeStreamSlotId,
      }
      return newMessages
    })
  }

  // Add empty assistant message that we'll update (retry reuses the slot reset in catch).
  // Agentic Continue: clear prior cappedAt so old "Continue working" banners do not stick,
  // and so merges never target the previous capped prose row.
  if (streamRetryAttempt === 0) {
    setStreamingMessages((prev) => {
      const base = agenticContinue
        ? prev.map((m) => (m.cappedAt ? { ...m, cappedAt: undefined } : m))
        : prev
      return [
        ...base,
        {
          role: 'assistant',
          content: '',
          timestamp: new Date(),
          toolResults: [],
          streamSlotId: activeStreamSlotId,
        },
      ]
    })
  }

  if (reader) {
    let buffer = '' // Buffer for incomplete SSE events

    readerLoop: while (true) {
      // Check if aborted
      if (abortController.signal.aborted) {
        if (stallWatchdog?.isStalled()) {
          throw new Error('network error')
        }
        devLog('[ChatPane] Stream aborted by user')
        reader.cancel()
        break
      }

      const { done, value } = await reader.read()
      if (done) {
        devLog('[ChatPane] Stream ended, total chunks:', streamChunkCount)
        break
      }

      const chunk = decoder.decode(value, { stream: true })
      streamChunkCount++
      stallWatchdog?.bump()

      // Add chunk to buffer
      buffer += chunk

      // Process complete SSE events (split by "\n\n")
      const events = buffer.split('\n\n')
      // Keep the last (potentially incomplete) event in the buffer
      buffer = events.pop() || ''

      for (const event of events) {
        if (!event.trim()) continue

        const lines = event.split('\n')
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6) // Remove "data: " prefix

            // Server sends nothing after [DONE]; awaiting the socket close lets a tail
            // reset (Chrome "network error") discard a fully received turn.
            if (data === '[DONE]') {
              devLog('[ChatPane] [DONE] received, total chunks:', streamChunkCount)
              stallWatchdog?.clear()
              reader.cancel().catch(() => {})
              break readerLoop
            }

            try {
              const parsed = JSON.parse(data)

            // Proxy idle keepalive (server emits alongside `: ping` comments). No UI.
            if (parsed.type === 'stream_heartbeat') {
              continue
            }

            if (parsed.type === 'context_usage') {
              const inputTokens = Number(parsed.input_tokens) || 0
              const contextWindow = Number(parsed.context_window) || 0
              if (contextWindow > 0 && params.onContextUsage) {
                params.onContextUsage({
                  inputTokens,
                  outputTokens: Number(parsed.output_tokens) || 0,
                  contextWindow,
                  estimated: parsed.estimated === true,
                  model: typeof parsed.model === 'string' ? parsed.model : null,
                  pack: Array.isArray(parsed.pack) ? parsed.pack : undefined,
                })
              }
            }

            if (parsed.type === 'agent_chat_thread' && parsed.threadId && parsed.conversationId) {
              const fromThreadId = hostedThreadIdRef?.current
              const toThreadId = String(parsed.threadId)
              if (fromThreadId && String(fromThreadId).startsWith('new-')) {
                window.dispatchEvent(
                  new CustomEvent('chat-thread-migrate', {
                    detail: {
                      fromThreadId,
                      toThreadId,
                    },
                  }),
                )
              } else if (
                claimAgentPanelOnNewThread &&
                (!fromThreadId || fromThreadId !== toThreadId)
              ) {
                window.dispatchEvent(
                  new CustomEvent('chat-surface-move', {
                    detail: { threadId: toThreadId, target: 'agent-panel' },
                  }),
                )
              }
              if (
                sidebarUpdateForAgentChatThread({ fromThreadId, toThreadId }) === 'refresh'
              ) {
                refreshChatsSidebar()
              } else {
                touchChatSidebarThread(toThreadId)
              }
              devLog('[ChatPane] agent_chat_thread:', parsed.threadId)
            }

            // Handle thinking mode events
            if (parsed.type === 'thinking_start') {
              setThinkingModeActive(true)
              setThinkingOperations([])
              setCurrentTask(null)
              // Do NOT clear completedFiles - prior-turn createDocument rows must stay in
              // the Files bar until Keep/Undo (meeting transcript then "go ahead" edits).
              setAgenticRound?.(null)
              const narration =
                typeof parsed.message === 'string' && parsed.message.trim()
                  ? parsed.message.trim()
                  : 'Planning next moves...'
              applyNarrationToAssistant(narration)
              devLog('[ChatPane] Thinking mode started:', narration)
            } else if (parsed.type === 'round_start') {
              const round = typeof parsed.round === 'number' ? parsed.round : 0
              const maxRounds = typeof parsed.maxRounds === 'number' ? parsed.maxRounds : 0
              if (round >= 1 && maxRounds > 0) {
                setAgenticRound?.({ current: round, max: maxRounds })
              }
              // Keep activity chrome visible while the next LLM round buffers/streams.
              // Wait copy lives on the thinking timeline (Searching / Reading / Thinking).
              setIsTyping(true)
            } else if (parsed.type === 'thinking_operation') {
              setThinkingModeActive(true)
              setThinkingOperations(prev => [...prev, parsed.operation])
              devLog('[ChatPane] Thinking operation:', parsed.operation)
            } else if (parsed.type === 'thinking_operation_update') {
              setThinkingOperations(prev =>
                prev.map(op =>
                  op.id === parsed.operationId
                    ? { ...op, ...parsed.updates }
                    : op
                )
              )
              devLog('[ChatPane] Thinking operation updated:', parsed.operationId, parsed.updates)
            } else if (parsed.type === 'thinking_code_generation') {
              setCurrentTask(parsed.task)
              devLog('[ChatPane] Code generation:', parsed.task)
            } else if (parsed.type === 'thinking_file_complete') {
              setCompletedFiles(prev => upsertCompletedFile(prev, parsed.file))
              if (parsed.file?.filePath) {
                recordTouchedFile({
                  filePath: parsed.file.filePath,
                  displayPath: parsed.file.displayPath,
                  url: parsed.file.url,
                  documentId: parsed.file.documentId,
                  title: parsed.file.title,
                  linesAdded: parsed.file.linesAdded,
                  linesRemoved: parsed.file.linesRemoved,
                  changeStatus: parsed.file.changeStatus,
                  status: parsed.file.status === 'new' ? 'new' : 'modified',
                })
              }
              devLog('[ChatPane] File complete:', parsed.file)
            } else if (parsed.type === 'thinking_complete') {
              setThinkingModeActive(false)
              setAgenticRound?.(null)
              // Don't clear isTyping here — keep spinner showing until actual text arrives
              devLog('[ChatPane] Thinking mode complete:', parsed.summary)
            }

            // Handle text content (live deltas, or paced reveal when a large blob arrives)
            if (parsed.type === 'text' && parsed.content) {
              if (isTypingRef.current) {
                setIsTyping(false)
              }
              const incoming = String(parsed.content)
              await revealTextProgressively(
                incoming,
                (slice) => {
                  if (narrationPlaceholderOnly) {
                    assistantMessage =
                      assistantMessage.slice(0, assistantSegmentStart) + slice
                    narrationPlaceholderOnly = false
                  } else {
                    assistantMessage += slice
                  }
                  flushAssistantStreamingSlot()
                },
                { signal: abortController.signal }
              )
            }

            // Validation rewrote the answer after live stream — replace current segment.
            // Skip shrinks of long prose (same guard as agentic-loop resolveAssistantTextEmit)
            // so mid-read content does not vanish into a shorter "summary".
            if (parsed.type === 'text_replace' && typeof parsed.content === 'string') {
              if (isTypingRef.current) {
                setIsTyping(false)
              }
              const currentSegment = assistantMessage.slice(assistantSegmentStart)
              const nextContent = String(parsed.content)
              const shouldSkipShrink =
                parsed.force !== true &&
                currentSegment.trim().length >= 400 &&
                nextContent.trim().length < currentSegment.trim().length * 0.9
              if (shouldSkipShrink) {
                console.warn(
                  '[ChatPane] Skipping text_replace shrink:',
                  currentSegment.length,
                  '->',
                  nextContent.length,
                )
              } else {
                assistantMessage =
                  assistantMessage.slice(0, assistantSegmentStart) + nextContent
                narrationPlaceholderOnly = false
                flushAssistantStreamingSlot()
              }
            }

            // Follow-up LLM call started — re-show typing indicator during buffering
            if (parsed.type === 'followup_start') {
              setIsTyping(true)
            }

            // Proposed writes (diff card / Basecamp draft): persist in toolTrace only, no tool card
            if (parsed.type === 'tool_trace' && typeof parsed.tool === 'string') {
              toolResults.push({
                tool: parsed.tool,
                args: parsed.args,
                result: parsed.result,
              })
              continue
            }

            // Handle tool calls - create inline tool messages
            if (parsed.type === 'tool_call') {
              sawToolCallEvent = true
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new Event('session-files-changed'))
              }
              devLog('[ChatPane] Tool called:', parsed.tool, parsed.args)

              // Store in toolResults for backward compatibility
              toolResults.push({
                tool: parsed.tool,
                args: parsed.args,
                result: parsed.result
              })

              if (
                parsed.tool === 'createBasecampTodo' &&
                parsed.result?.success &&
                parsed.result?.dryRun !== true &&
                (parsed.result?.todoUrl || parsed.result?.todo?.url)
              ) {
                setPendingBasecampCreateDraft?.(null)
              }

              if (
                parsed.tool === 'updateBasecampTodoNotes' &&
                parsed.result?.success
              ) {
                window.dispatchEvent(new CustomEvent('triage-due-date-updated', {
                  detail: { todoUrl: parsed.args?.todoUrl }
                }))
                dispatchBasecampTodoRefreshFromNotesWrite({
                  todoId: parsed.result?.todoId ?? parsed.result?.todo?.id,
                  todoUrl: parsed.args?.todoUrl ?? parsed.result?.todoUrl,
                })
              }

              // Create inline tool message BEFORE assistant response
              const toolMessage: Message = {
                role: 'tool',
                content: parsed.result?.error ? String(parsed.result.error) : '',
                toolName: parsed.tool,
                toolCallId:
                  typeof parsed.tool_call_id === 'string'
                    ? parsed.tool_call_id
                    : typeof parsed.toolCallId === 'string'
                      ? parsed.toolCallId
                      : undefined,
                toolArgs: parsed.args,
                toolDisplayLabel: typeof parsed.label === 'string' ? parsed.label : undefined,
                toolResult: parsed.result,
                ...(parsed.tool === 'createDocument' && parsed.args?.content != null && {
                  outputContent: parsed.args.content
                }),
                timestamp: new Date(),
                ...(parsed.batch_id && {
                  batchId: parsed.batch_id,
                  batchIndex: parsed.batch_index,
                  batchSize: parsed.batch_size,
                }),
              }

              // Debug logging for createDocument
              if (parsed.tool === 'createDocument') {
                devLog('[ChatPane] createDocument tool result:', {
                  parsed,
                  result: parsed.result,
                  toolMessage
                })
              }

              // Add tool message to chat - only the active stream slot (never prior capped prose).
              // Empty / narration slot: insert tool before it. Filled slot: seal, push tool, open
              // a fresh streamSlotId assistant below for the next prose segment.
              setStreamingMessages(prev => {
                const ensured = ensureActiveStreamSlot([...prev])
                const newMessages = ensured.messages
                const aiIdx = ensured.index
                const lastAssistant = newMessages[aiIdx]
                const effectivelyEmpty =
                  !lastAssistant.content?.trim() || !!lastAssistant.narrationPlaceholder
                if (effectivelyEmpty) {
                  if (lastAssistant.narrationPlaceholder || lastAssistant.content?.trim()) {
                    newMessages[aiIdx] = {
                      ...lastAssistant,
                      content: '',
                      narrationPlaceholder: false,
                      streamSlotId: activeStreamSlotId,
                    }
                    narrationPlaceholderOnly = false
                    assistantMessage = assistantMessage.slice(0, assistantSegmentStart)
                  }
                  newMessages.splice(aiIdx, 0, toolMessage)
                } else {
                  newMessages.push(toolMessage)
                  assistantSegmentStart = assistantMessage.length
                  pendingActionChoices = null
                  pendingAskQuestion = null
                  pendingFlashCardSession = null
                  pendingQuizCreated = null
                  const freshId = nextStreamSlotId()
                  newMessages.push({
                    role: 'assistant',
                    content: '',
                    timestamp: new Date(),
                    toolResults: [],
                    streamSlotId: freshId,
                    actionChoices: undefined,
                    askQuestion: undefined,
                    flashCardSession: undefined,
                    quizCreated: undefined,
                  })
                }
                return newMessages
              })

              // Extract documents from search results for inline rendering
              if (parsed.tool === 'searchDocuments' && parsed.result) {
                // Handle both 'documents' array and 'results' array formats
                const docs = parsed.result.documents || parsed.result.results || []
                docs.forEach((doc: any) => {
                  const id = doc?.id != null ? String(doc.id) : ''
                  // FlowNotes document UUIDs only (never Basecamp numeric ids)
                  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
                    return
                  }
                  documentsMap.set(id, {
                    id,
                    title: doc.title,
                    type: doc.type,
                    path: doc.path || doc.filePath,
                    excerpt: doc.excerpt
                  })
                })
                // Debug logging
                if (docs.length > 0) {
                  devLog('[ChatPane] Added documents to map:', docs.length, 'documents')
                }
              }

              // Also handle getDocument results
              if (parsed.tool === 'getDocument' && parsed.result?.document) {
                const doc = parsed.result.document
                const id = doc?.id != null ? String(doc.id) : ''
                if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
                  documentsMap.set(id, {
                    id,
                    title: doc.title,
                    type: doc.type,
                    path: doc.path
                  })
                }
              }

              // Show toast notification for important actions (keep for visibility)
              if (parsed.result?.success) {
                if (parsed.tool === 'archiveDocument') {
                  toast.success('Document archived', {
                    description: parsed.result.documentId ? 'Document moved to archive' : undefined
                  })
                } else if (parsed.tool === 'moveDocument') {
                  const moveDocId =
                    parsed.result.documentId ||
                    parsed.result.document?.id ||
                    parsed.args?.documentId
                  const moveOldPath =
                    parsed.result.oldPath ||
                    parsed.result.document?.oldPath ||
                    activeDocumentIdRef.current
                  const moveNewPath =
                    parsed.result.newPath ||
                    parsed.result.document?.newPath ||
                    parsed.args?.newPath
                  const moveTitle =
                    parsed.result.title ||
                    parsed.result.document?.title ||
                    undefined

                  toast.success('Document moved', {
                    description: moveNewPath || undefined
                  })

                  if (moveDocId && moveNewPath) {
                    if (moveOldPath) {
                      window.dispatchEvent(new CustomEvent('document-moved', {
                        detail: { oldPath: moveOldPath, newPath: moveNewPath }
                      }))
                      window.dispatchEvent(new CustomEvent('document-path-updated', {
                        detail: {
                          documentId: moveDocId,
                          oldPath: moveOldPath,
                          newPath: moveNewPath,
                          title: moveTitle
                        }
                      }))
                    }
                    window.dispatchEvent(new CustomEvent('document-updated', {
                      detail: { documentId: moveDocId, filePath: moveNewPath }
                    }))
                  }
                } else if (parsed.tool === 'createDocument') {
                  toast.success('Document created', {
                    description: parsed.result.title || parsed.result.document?.title || undefined
                  })
                  const createdId = String(
                    parsed.result.documentId || parsed.result.document?.id || '',
                  )
                  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(createdId)) {
                    documentsMap.set(createdId, {
                      id: createdId,
                      title: parsed.result.title || parsed.result.document?.title,
                      type: parsed.result.document?.type || parsed.result.documentType,
                      path: parsed.result.filePath || parsed.result.document?.filePath,
                    })
                  }
                  // Refresh file tree and document list
                  window.dispatchEvent(new CustomEvent('document-created', {
                    detail: {
                      documentId: parsed.result.documentId || parsed.result.document?.id,
                      filePath: parsed.result.filePath || parsed.result.document?.filePath,
                      title: parsed.result.title || parsed.result.document?.title
                    }
                  }))
                } else if (parsed.tool === 'appendToDocument') {
                  toast.success('Content appended', {
                    description: parsed.result.documentId ? 'Content added to document' : undefined
                  })
                }
                // Note: updateDocument/appendToDocument proposed edits use proposed_edit SSE (no tool_call toast)
              } else if (parsed.result?.error) {
                // Tool failures are normal agent operation (retry, fallback, etc.).
                // Already shown inline in the chat step UI — no toast needed.
                console.warn('[ChatPane] Tool error (agent will handle):', parsed.tool, parsed.result.error)
              }

              // Handle document modification tools - trigger reload
              if (parsed.result?.success && parsed.result?.documentId) {
                const modificationTools = ['appendToDocument', 'updateDocument', 'updateDocumentFrontmatter']
                if (modificationTools.includes(parsed.tool)) {
                  devLog('[ChatPane] Document modified by tool:', parsed.tool, 'documentId:', parsed.result.documentId)

                  // Dispatch event to trigger document reload
                  window.dispatchEvent(new CustomEvent('document-updated', {
                    detail: { documentId: parsed.result.documentId }
                  }))
                }
              }
            }

            // Handle document_list event (backend sends structured doc data directly)
            if (parsed.type === 'document_list' && parsed.documents) {
              devLog('[ChatPane] Document list received:', parsed.documents.length, 'documents')
              // Add all documents to the documentsMap for inline rendering
              for (const doc of parsed.documents) {
                const id = doc?.id != null ? String(doc.id) : ''
                if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
                  continue
                }
                documentsMap.set(id, {
                  id,
                  title: doc.title,
                  type: doc.type || 'note',
                  path: doc.path
                })
              }
            }

            // Image search gallery is rendered on the imageSearch tool_call card;
            // log the SSE for diagnostics (same payload as tool result.results).
            if (parsed.type === 'image_search_results' && Array.isArray(parsed.results)) {
              devLog('[ChatPane] Image search results:', parsed.results.length, 'query:', parsed.query)
            }

            // Handle action_choices — attach to the current assistant segment only (do not leak to later segments)
            if (parsed.type === 'action_choices' && Array.isArray(parsed.choices)) {
              const choices = enrichActionChoices(
                parsed.choices as ActionChoice[],
                documentsMap,
              )
              devLog('[ChatPane] Action choices received:', choices.length)
              let attachedToSlot = false
              setStreamingMessages((prev) => {
                const ensured = ensureActiveStreamSlot([...prev])
                const next = ensured.messages
                const idx = ensured.index
                next[idx] = { ...next[idx], actionChoices: choices }
                attachedToSlot = true
                return next
              })
              pendingActionChoices = attachedToSlot ? null : choices
            }

            if (parsed.type === 'ask_question' && parsed.question && Array.isArray(parsed.options)) {
              const askQuestion = {
                questionId: String(parsed.questionId || `q-${Date.now()}`),
                question: String(parsed.question),
                options: parsed.options as AskQuestionData['options'],
                allowMultiple: !!parsed.allowMultiple,
                allowOther: parsed.allowOther !== false,
                replyTemplate: parsed.replyTemplate
                  ? String(parsed.replyTemplate)
                  : undefined,
              }
              setStreamingMessages((prev) => {
                const ensured = ensureActiveStreamSlot([...prev])
                const next = ensured.messages
                next[ensured.index] = { ...next[ensured.index], askQuestion }
                return next
              })
              pendingAskQuestion = askQuestion
            }

            if (
              parsed.type === 'flash_card_session' &&
              Array.isArray(parsed.cards) &&
              parsed.cards.length > 0
            ) {
              const flashCardSession: FlashCardSessionData = {
                sessionId: String(parsed.sessionId || `flash-${Date.now()}`),
                slug: String(parsed.slug || ''),
                documentId: parsed.documentId ? String(parsed.documentId) : null,
                card: parsed.card || parsed.cards[0],
                cards: parsed.cards,
                progress: parsed.progress || { index: 0, total: parsed.cards.length },
              }
              setStreamingMessages((prev) => {
                const ensured = ensureActiveStreamSlot([...prev])
                const next = ensured.messages
                next[ensured.index] = { ...next[ensured.index], flashCardSession }
                return next
              })
              pendingFlashCardSession = flashCardSession
            }

            if (
              parsed.type === 'quiz_document_created' &&
              parsed.documentId
            ) {
              const quizCreated: QuizDocumentCreatedData = {
                documentId: String(parsed.documentId),
                filePath: parsed.filePath ? String(parsed.filePath) : null,
                title: parsed.title ? String(parsed.title) : null,
                slug: parsed.slug ? String(parsed.slug) : null,
                questionCount:
                  typeof parsed.questionCount === 'number' ? parsed.questionCount : null,
              }
              documentsMap.set(quizCreated.documentId, {
                id: quizCreated.documentId,
                title: quizCreated.title || 'Quiz',
                type: 'quiz',
                path: quizCreated.filePath || undefined,
              })
              setStreamingMessages((prev) => {
                const ensured = ensureActiveStreamSlot([...prev])
                const next = ensured.messages
                next[ensured.index] = { ...next[ensured.index], quizCreated }
                return next
              })
              pendingQuizCreated = quizCreated
            }

            // Backend auto-detected and loaded a skill — reflect it in the UI
            // Clears the suggestion badge (it was already running) and sets the
            // active chip so the sent message shows which skill ran
            if (parsed.type === 'skill_auto_activated' && parsed.skillId) {
              autoSkillActivatedThisStream = true
              const matchedSkill = scopedAvailableSkills.find(s => s.id === parsed.skillId)
              if (matchedSkill) {
                setActiveChatSkill({ id: matchedSkill.id, name: matchedSkill.name })
                setSuggestedSkill?.(null)
              }
            }

            if (
              parsed.type === 'scope_expanded' &&
              ((Array.isArray(parsed.addedLabels) && parsed.addedLabels.length > 0) ||
                (Array.isArray(parsed.addedPaths) && parsed.addedPaths.length > 0))
            ) {
              const labels =
                Array.isArray(parsed.addedLabels) && parsed.addedLabels.length > 0
                  ? parsed.addedLabels.join(', ')
                  : (parsed.addedPaths as string[]).join(', ')
              toast.info('Chat scope expanded for this skill', {
                description: `Also searching: ${labels}`,
                duration: 6000,
              })
            }

            // Handle edit intent detection warning
            if (parsed.type === 'edit_intent_detected') {
              devLog('[ChatPane] Edit intent detected:', parsed.message)
              toast.warning('AI edit intent detected', {
                description: parsed.message || 'Attempting to ensure updateDocument is called...',
                duration: 5000
              })
            }

            // Basecamp notes draft from chat agent (proposeBasecampTodoNotes)
            if (parsed.type === 'proposed_basecamp_update') {
              const todoId = String(parsed.todoId || '')
              const html = String(parsed.html || '')
              const todoUrl = String(parsed.todoUrl || '')
              if (todoId && html && todoUrl) {
                devLog('[ChatPane] Proposed Basecamp update received:', todoId)
                const draft: BasecampNotesDraft = {
                  todoId,
                  html,
                  todoUrl,
                  title: parsed.title || undefined,
                }
                setPendingBasecampDraft?.(draft)
                const threadId =
                  hostedThreadIdRef?.current ??
                  streamingConversationId ??
                  undefined
                window.dispatchEvent(
                  new CustomEvent('basecamp-draft-ready', {
                    detail: {
                      ...draft,
                      threadId,
                    },
                  })
                )
                setTimeout(() => {
                  if (userHasScrolledRef?.current) return
                  const el = messagesContainerRef.current
                  if (!el) return
                  const distanceFromBottom =
                    el.scrollHeight - el.scrollTop - el.clientHeight
                  // Match useScrollDetection near-bottom threshold (160px)
                  if (distanceFromBottom < 160) {
                    el.scrollTop = el.scrollHeight
                  }
                }, 50)
              }
              continue
            }

            if (parsed.type === 'proposed_basecamp_create') {
              const draft = parseProposedBasecampCreateEvent(parsed as Record<string, unknown>)
              if (draft) {
                devLog('[ChatPane] Proposed Basecamp create received:', draft.todolistUrl)
                pendingCreateDraftForPersist = draft
                persistBasecampCreateDraft(savedConvId, draft)
                persistBasecampCreateDraft(streamingConversationId, draft)
                persistBasecampCreateDraft(hostedThreadIdRef?.current, draft)
                setPendingBasecampCreateDraft?.(draft)
                setTimeout(() => {
                  if (userHasScrolledRef?.current) return
                  const el = messagesContainerRef.current
                  if (!el) return
                  const distanceFromBottom =
                    el.scrollHeight - el.scrollTop - el.clientHeight
                  if (distanceFromBottom < 160) {
                    el.scrollTop = el.scrollHeight
                  }
                }, 50)
              }
              continue
            }

            // Handle proposed edits - show diff preview
            if (parsed.type === 'proposed_edit') {
              // Use parsed.documentId or fallback to activeDocumentId (read at event time)
              const documentId = parsed.documentId || activeDocumentIdRef.current

              if (!documentId) {
                console.error('[ChatPane] Proposed edit received but no documentId available', parsed)
                toast.error('Cannot show edit preview', {
                  description: 'Document ID is missing'
                })
                continue
              }

              devLog('[ChatPane] Proposed edit received:', documentId)

              const changedLinesPayload = normalizeChangedLines(parsed.changedLines ?? parsed.changed_lines)
              let additions = changedLinesPayload ? changedLinesPayload.added.length : 0
              let deletions = changedLinesPayload ? changedLinesPayload.removed.length : 0

              if (!changedLinesPayload) {
                const { diffLines } = await import('diff')
                const changes = diffLines(parsed.original || '', parsed.proposed || '')
                changes.forEach(change => {
                  if (change.added) additions += change.count || 0
                  if (change.removed) deletions += change.count || 0
                })
              }

              const priorSameDoc = sessionProposedEdits.find(
                (e) => e.documentId === documentId && e.status === 'pending',
              )
              // Keep the earliest original when the backend chains same-doc patches into one review.
              const reviewOriginal = priorSameDoc?.original || parsed.original || ''

              let coalescedAdditions = additions
              let coalescedDeletions = deletions
              if (priorSameDoc && reviewOriginal !== (parsed.original || '')) {
                try {
                  const { diffLines } = await import('diff')
                  const changes = diffLines(reviewOriginal, parsed.proposed || '')
                  coalescedAdditions = 0
                  coalescedDeletions = 0
                  changes.forEach((change) => {
                    if (change.added) coalescedAdditions += change.count || 0
                    if (change.removed) coalescedDeletions += change.count || 0
                  })
                } catch {
                  // keep server-provided counts
                }
              }

              const editData: ProposedEditData = {
                documentId: documentId,
                filename: parsed.filename || 'document.md',
                displayPath: parsed.displayPath ?? null,
                url: parsed.url ?? null,
                title: parsed.title || 'Document',
                original: reviewOriginal,
                proposed: parsed.proposed || '',
                frontmatter: parsed.frontmatter,
                explanation: parsed.explanation,
                status: 'pending',
                additions: coalescedAdditions,
                deletions: coalescedDeletions,
                changedLines: changedLinesPayload,
                changeStatus: changedLinesPayload?.change_status,
                requiresAcceptance: changedLinesPayload?.requires_acceptance ?? true,
                conversationId: streamingConversationId,
                sessionEditId:
                  priorSameDoc?.sessionEditId ||
                  `${documentId}-${sessionProposedEdits.length}-${Date.now()}`,
              }

              const existingSessionIdx = sessionProposedEdits.findIndex(
                (e) => e.documentId === documentId && e.status === 'pending',
              )
              if (existingSessionIdx >= 0) {
                sessionProposedEdits[existingSessionIdx] = editData
              } else {
                sessionProposedEdits.push(editData)
              }

              // One pending card per document - replace prior same-doc pending card.
              // Insert before the *active stream* slot so Continue never treats the card as
              // the live prose target, and prior capped assistants stay above the card.
              setStreamingMessages(prev => {
                const withoutPrior = prev.filter(
                  (m) =>
                    !(
                      m.proposedEdit?.documentId === documentId &&
                      m.proposedEdit?.status === 'pending'
                    ),
                )
                const ensured = ensureActiveStreamSlot([...withoutPrior])
                const messages = ensured.messages
                const aiIdx = ensured.index
                const editCard: Message = {
                  role: 'assistant',
                  content: '',
                  proposedEdit: editData,
                  timestamp: new Date(),
                }
                messages.splice(aiIdx, 0, editCard)
                return messages
              })

              // Do not recordTouchedFile for pending proposed edits - they are not applied
              // until Accept. Recording here made Reject leave ghost rows and Review claim
              // "already written". Accept path upserts completedFiles instead.
            }

            // Handle checkpoints summary — attach to current assistant message for undo UI
            if (parsed.type === 'checkpoints_summary' && Array.isArray(parsed.checkpointIds) && parsed.checkpointIds.length > 0) {
              setStreamingMessages(prev => {
                const msgs = [...prev]
                const last = msgs[msgs.length - 1]
                if (last?.role === 'assistant') {
                  msgs[msgs.length - 1] = { ...last, checkpointIds: parsed.checkpointIds }
                }
                return msgs
              })
            }

            // Handle errors from stream (e.g. API parameter or provider errors)
            if (parsed.type === 'error') {
              const formatted = formatChatStreamError(parsed as Parameters<typeof formatChatStreamError>[0])
              const isAgenticCap =
                parsed.error === 'max_rounds_exceeded' ||
                parsed.error === 'tool_budget_exceeded' ||
                parsed.error === 'incomplete_write'
              if (isAgenticCap) {
                devLog('[ChatPane] Agentic cap:', parsed.error, formatted.description)
              } else {
                console.error('[ChatPane] Stream error:', formatted.title, formatted.description)
              }

              if (isAgenticCap) {
                // Cap is not terminal — backend may still stream synthesis text after this event.
                narrationPlaceholderOnly = false
                streamCappedAt =
                  parsed.error === 'tool_budget_exceeded'
                    ? 'max_tools'
                    : parsed.error === 'incomplete_write'
                      ? 'incomplete_write'
                      : 'max_rounds'
                setStreamingMessages(prev => {
                  const ensured = ensureActiveStreamSlot([...prev])
                  const next = ensured.messages
                  const aiIdx = ensured.index
                  next[aiIdx] = {
                    ...next[aiIdx],
                    cappedAt: streamCappedAt,
                    narrationPlaceholder: false,
                    streamSlotId: activeStreamSlotId,
                  }
                  return next
                })
              } else {
                toast.error(formatted.title, {
                  description: formatted.description,
                  duration: 12000,
                })
                setStreamingMessages(prev => {
                  const ensured = ensureActiveStreamSlot([...prev])
                  const next = ensured.messages
                  const aiIdx = ensured.index
                  const last = next[aiIdx]
                  next[aiIdx] = {
                    ...last,
                    content:
                      (last.content || '') +
                      (last.content ? '\n\n' : '') +
                      formatted.inlineMarkdown,
                    narrationPlaceholder: false,
                    streamSlotId: activeStreamSlotId,
                  }
                  return next
                })
                break readerLoop
              }
            }
            } catch (e) {
              // JSON parse failed - log and skip
              console.error('[ChatPane] Failed to parse SSE data:', data, e)
            }
          }
        }
      }

      // Tool / metadata updates that did not go through text progressive flush
      flushAssistantStreamingSlot()
    }
  }

  devLog('[ChatPane] Streaming complete, final message length:', assistantMessage.length);

  // Final merge into streaming assistant slot (tools may still trail that slot)
  const touchedMetaForLive = buildTouchedFilesMetadata(touchedFilesForPersist)
  const toolTraceMeta = buildToolTraceMetadata(toolResults)
  const effectiveCappedAt =
    streamCappedAt ?? inferCappedAtFromAssistantContent(assistantMessage)
  const cleanedAssistantMessage = stripAgenticPauseProse(assistantMessage)
  const liveMeta = {
    ...(touchedMetaForLive || {}),
    ...(toolTraceMeta || {}),
    ...(effectiveCappedAt ? { cappedAt: effectiveCappedAt } : {}),
    ...(pendingCreateDraftForPersist
      ? { basecampCreateDraft: pendingCreateDraftForPersist }
      : {}),
  }
  setStreamingMessages(prev => {
    const restored = prependMissingUserTurns(prev, messagesForHistory)
    const ensured = ensureActiveStreamSlot([...restored])
    const newMessages = ensured.messages
    const idx = ensured.index
    const slotEl = newMessages[idx]
    const displayContent = stripAgenticPauseProse(assistantMessage.slice(assistantSegmentStart))
    if (slotEl?.role === 'assistant') {
      const session = getActiveTodoActionSession()
      const hostedId = hostedThreadIdRef?.current
      const isDraftSession =
        session?.kind === 'draft_comment' &&
        Boolean(hostedId) &&
        session.threadId === hostedId
      const askUnanswered = Boolean(
        (pendingAskQuestion ?? slotEl.askQuestion) &&
          !(pendingAskQuestion ?? slotEl.askQuestion)?.answered,
      )
      const nextChoices = isDraftSession
        ? attachFillCommentChoice(pendingActionChoices ?? slotEl.actionChoices, askUnanswered)
        : pendingActionChoices ?? slotEl.actionChoices
      newMessages[idx] = {
        ...slotEl,
        role: 'assistant',
        content: displayContent,
        toolResults: toolResults,
        documentsMap: documentsMap,
        actionChoices: nextChoices,
        askQuestion: pendingAskQuestion ?? slotEl.askQuestion,
        flashCardSession: pendingFlashCardSession ?? slotEl.flashCardSession,
        quizCreated: pendingQuizCreated ?? slotEl.quizCreated,
        narrationPlaceholder: false,
        streamSlotId: activeStreamSlotId,
        cappedAt: effectiveCappedAt ?? slotEl.cappedAt,
        metadata: Object.keys(liveMeta).length
          ? { ...(slotEl.metadata || {}), ...liveMeta }
          : slotEl.metadata,
      }
    }
    return newMessages
  })

  // Save assistant message to conversation history.
  // User message was already saved before streaming started (unless agenticContinue).
  // Skip thinking_start narration ("Working on: ...") as the reply.
  if ((cleanedAssistantMessage.trim() || pendingAskQuestion || pendingFlashCardSession) && !narrationPlaceholderOnly) {
    await stampSavedAssistant(assistantMessage, {
      askQuestion: pendingAskQuestion,
      flashCardSession: pendingFlashCardSession,
    })
  }

  // Auto-name after each completed assistant reply while title is still the default.
  // Do not gate on message count from messagesForHistory - that snapshot is stale at stream end.
  await maybeAutoNameConversation()

} catch (error: any) {
  // Handle abort error separately — not a real error
  const stalled = stallWatchdog?.isStalled() === true
  const isAbort = !stalled && (
    error.name === 'AbortError' ||
    error.name === 'TimeoutError' ||
    error.message?.includes('aborted') ||
    error.message?.includes('This operation was aborted')
  )
  if (isAbort) {
    devLog('[ChatPane] Request aborted by user')
    // Mark the last assistant message as stopped (if handleStop didn't already)
    setStreamingMessages(prev => {
      const msgs = [...prev]
      const last = msgs[msgs.length - 1]
      if (last?.role === 'assistant' && !last.aborted) {
        msgs[msgs.length - 1] = { ...last, aborted: true }
      }
      return msgs
    })
  } else {
    const transportDrop = stalled || isChatStreamTransportError(error)
    const worthKeeping = transportDrop
      ? stripAgenticPauseProse(
          assistantTextWorthKeeping(
            assistantMessage,
            assistantSegmentStart,
            narrationPlaceholderOnly,
          ),
        )
      : ''
    // Tools block a blind retry. They must not discard a reply that already arrived.
    // The [DONE] break never runs when reader.read() throws on the chunk that carries it.
    const recovery = transportDrop
      ? decideChatStreamTailRecovery({
          recoverableText: worthKeeping,
          streamRetryAttempt,
          userAborted: abortController.signal.aborted,
          stalled,
          sawToolCallEvent,
          proposedEditCount: sessionProposedEdits.length,
          touchedFileCount: touchedFilesForPersist.length,
        })
      : 'banner'

    if (recovery === 'retry') {
      devLog(
        `[ChatPane] Mid-stream transport drop (chunks=${streamChunkCount}) with no side effects - retrying once`,
      )
      toast.message('Reconnecting...', {
        description: 'Chat stream dropped; retrying once.',
      })
      setThinkingOperations([])
      setCurrentTask(null)
      setStreamingMessages((prev) => {
        const ensured = ensureActiveStreamSlot([...prev])
        const msgs = ensured.messages
        msgs[ensured.index] = {
          role: 'assistant',
          content: '',
          timestamp: new Date(),
          toolResults: [],
          narrationPlaceholder: true,
          streamSlotId: activeStreamSlotId,
        }
        return msgs
      })
      // Fresh abort controller for the retry (previous stream is dead).
      pendingRetryController = new AbortController()
      abortControllerRef.current = pendingRetryController
    } else if (recovery === 'keep') {
      devLog(
        `[ChatPane] Transport drop after reply text (chunks=${streamChunkCount}, chars=${worthKeeping.length}) - persisting`,
      )
      toast.message(CHAT_STREAM_REPLY_KEPT_TOAST.title, {
        description: CHAT_STREAM_REPLY_KEPT_TOAST.description,
      })
      if (narrationPlaceholderOnly) {
        setStreamingMessages((prev) => {
          const ensured = ensureActiveStreamSlot([...prev])
          const slot = ensured.messages[ensured.index]
          if (slot?.role === 'assistant') {
            ensured.messages[ensured.index] = {
              ...slot,
              content: worthKeeping,
              narrationPlaceholder: false,
              streamSlotId: activeStreamSlotId,
            }
          }
          return ensured.messages
        })
      }
      try {
        await stampSavedAssistant(worthKeeping, {
          askQuestion: pendingAskQuestion,
          flashCardSession: pendingFlashCardSession,
        })
        await maybeAutoNameConversation()
      } catch (persistErr) {
        console.error('[ChatPane] Failed to persist reply after stream tail drop:', persistErr)
      }
    } else {
      streamEndedWithError = true
      const friendly = transportDrop
        ? formatChatStreamTransportError()
        : {
            title: 'Chat error',
            description: error instanceof Error ? error.message : 'Failed to send message',
          }
      setStreamingMessages(prev => {
        const ensured = ensureActiveStreamSlot([...prev])
        const newMessages = ensured.messages
        newMessages[ensured.index] = {
          role: 'assistant',
          content: `⚠️ **${friendly.title}**: ${friendly.description}`,
          timestamp: new Date(),
          streamSlotId: activeStreamSlotId,
        }
        return newMessages
      })

      toast.error(friendly.title, {
        description: friendly.description,
      });
    }
  }
  } finally {
    stallWatchdog?.clear()
    if (pendingRetryController) {
      // Keep isStreaming / typing active; retry path owns cleanup.
    } else {
      const ownsPane = streamStillOwnsPane()
      if (ownsPane) {
        // Ensure interim narration placeholder never sticks after stream ends.
        setStreamingMessages(prev => {
          const next = [...prev]
          const idx = slotIdx(next)
          if (idx >= 0 && next[idx]?.role === 'assistant' && next[idx].narrationPlaceholder) {
            next[idx] = { ...next[idx], narrationPlaceholder: false }
          }
          return next
        })
        if (!streamEndedWithError && sessionProposedEdits.length > 0) {
          onStreamProposedEditsComplete?.(sessionProposedEdits)
        }
        setIsStreaming(false)
        setIsTyping(false)
        setAgenticRound?.(null)
        if (autoSkillActivatedThisStream) {
          setActiveChatSkill(null)
        }
        if (savedConvId) {
          window.dispatchEvent(new Event('session-files-changed'))
        }
      }
      // Only clear the ref if we still own it (a newer send may have replaced the controller).
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null
      }
    }
  }

  if (pendingRetryController) {
    return executeGeneralChatStream({
      ...params,
      abortController: pendingRetryController,
      streamRetryAttempt: 1,
    })
  }

  return { cappedAt: streamCappedAt }
}
